/**
 * Planning and authorizing a reorganization (mig 732). Every write is an RPC:
 * a reorg is an authority transition, so the client never writes these tables
 * (build contract §2.3a). The server re-checks admin authority and the
 * structure_reorg module opt-in on every call.
 */

import { getTypedSupabaseClient } from '../typedSupabaseClient';
import type { Json } from '../../types/database.generated';
import type { HierarchyKind } from './hierarchyTypes';
import type { DraftDisposition, DraftNode, DepartureDisposition } from './structureDraft';

export type ReferenceClass = 'standing' | 'in_flight' | 'authority';

export interface ImpactReference {
  refType: string;
  label: string;
  klass: ReferenceClass;
  count: number;
  refIds: string[];
  /** Standing references on a merged unit move to its successor on their own. */
  autoCarry: boolean;
  resolved: number;
}

export interface ImpactDeparted {
  nodeId: string;
  name: string;
  pathNames: string[];
  disposition: DepartureDisposition | null;
  successorIds: string[];
  references: ImpactReference[];
}

export interface StructureDraftImpact {
  draftId: string;
  baseRevisionId: string | null;
  baseIsStale: boolean;
  departed: ImpactDeparted[];
  missingDispositions: Array<{ nodeId: string; name: string }>;
  unresolvedCount: number;
  depthChanges: Array<{ nodeId: string; name: string; fromDepth: number; toDepth: number; grants: number }>;
}

export interface OpenStructureDraft {
  id: string;
  label: string | null;
  rationale: string | null;
  baseRevisionId: string | null;
  major: number;
  nodes: DraftNode[];
  base: DraftNode[];
  dispositions: DraftDisposition[];
}

export interface AuthorizeResult {
  revisionId: string;
  version: string;
  added: number;
  changed: number;
  retired: number;
  referencesMoved: number;
  unresolvedOverridden: number;
}

type Rec = Record<string, unknown>;
const asArray = (v: unknown): Rec[] => (Array.isArray(v) ? (v as Rec[]) : []);

function toImpact(raw: unknown): StructureDraftImpact {
  const r = (raw ?? {}) as Rec;
  return {
    draftId: String(r.draft_id ?? ''),
    baseRevisionId: (r.base_revision_id as string | null) ?? null,
    baseIsStale: Boolean(r.base_is_stale),
    unresolvedCount: Number(r.unresolved_count ?? 0),
    missingDispositions: asArray(r.missing_dispositions).map((m) => ({ nodeId: String(m.node_id), name: String(m.name) })),
    depthChanges: asArray(r.depth_changes).map((d) => ({
      nodeId: String(d.node_id),
      name: String(d.name),
      fromDepth: Number(d.from_depth),
      toDepth: Number(d.to_depth),
      grants: Number(d.grants ?? 0),
    })),
    departed: asArray(r.departed).map((d) => ({
      nodeId: String(d.node_id),
      name: String(d.name),
      pathNames: Array.isArray(d.path_names) ? (d.path_names as string[]) : [],
      disposition: (d.disposition as DepartureDisposition | null) ?? null,
      successorIds: asArray(d.successors).map((s) => String(s.to_node_id)),
      references: asArray(d.references).map((ref) => ({
        refType: String(ref.ref_type),
        label: String(ref.label),
        klass: ref.klass as ReferenceClass,
        count: Number(ref.count ?? 0),
        refIds: Array.isArray(ref.ref_ids) ? (ref.ref_ids as string[]) : [],
        autoCarry: Boolean(ref.auto_carry),
        resolved: Number(ref.resolved ?? 0),
      })),
    })),
  };
}

function friendly(message: string): string {
  return message.replace(/^.*?ERROR:\s*/i, '');
}

async function loadNodes(revisionId: string): Promise<DraftNode[]> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db.from('structure_revision_nodes').select('*').eq('revision_id', revisionId);
  if (error) throw new Error(`Failed to load structure: ${error.message}`);
  return (data ?? []).map((row) => ({
    nodeId: row.node_id,
    parentNodeId: row.parent_node_id,
    name: row.name,
    level: row.level,
    sortOrder: row.sort_order,
  }));
}

/** The open reorganization draft for this tree, if an admin has one. */
export async function getOpenStructureDraft(workspaceId: string, kind: HierarchyKind): Promise<OpenStructureDraft | null> {
  const db = getTypedSupabaseClient();
  const { data: rev, error } = await db
    .from('structure_revisions')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('hierarchy_kind', kind)
    .eq('status', 'draft')
    .maybeSingle();
  if (error) throw new Error(`Failed to load the draft: ${error.message}`);
  if (!rev) return null;

  const [nodes, base, disp, succ] = await Promise.all([
    loadNodes(rev.id),
    rev.supersedes_revision_id ? loadNodes(rev.supersedes_revision_id) : Promise.resolve([]),
    db.from('structure_node_dispositions').select('*').eq('revision_id', rev.id),
    db.from('structure_node_successors').select('*').eq('revision_id', rev.id),
  ]);
  if (disp.error) throw new Error(`Failed to load the draft: ${disp.error.message}`);
  if (succ.error) throw new Error(`Failed to load the draft: ${succ.error.message}`);

  return {
    id: rev.id,
    label: rev.label,
    rationale: rev.rationale,
    baseRevisionId: rev.supersedes_revision_id,
    major: rev.major,
    nodes,
    base,
    dispositions: (disp.data ?? []).map((d) => ({
      fromNodeId: d.from_node_id,
      disposition: d.disposition as DepartureDisposition,
      successorIds: (succ.data ?? []).filter((s) => s.from_node_id === d.from_node_id).map((s) => s.to_node_id),
      note: d.note ?? undefined,
    })),
  };
}

export async function createStructureDraft(workspaceId: string, kind: HierarchyKind, label?: string, rationale?: string): Promise<string> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db.rpc('create_structure_draft', {
    p_workspace_id: workspaceId,
    p_kind: kind,
    p_label: label,
    p_rationale: rationale,
  });
  if (error) throw new Error(friendly(error.message));
  return data;
}

export async function saveStructureDraft(
  draftId: string,
  nodes: readonly DraftNode[],
  dispositions: readonly DraftDisposition[],
  meta?: { label?: string; rationale?: string },
): Promise<StructureDraftImpact> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db.rpc('save_structure_draft', {
    p_draft_id: draftId,
    p_nodes: nodes.map((n) => ({
      node_id: n.nodeId,
      parent_node_id: n.parentNodeId,
      name: n.name,
      level: n.level,
      sort_order: n.sortOrder,
    })) as unknown as Json,
    p_dispositions: dispositions.map((d) => ({
      from_node_id: d.fromNodeId,
      disposition: d.disposition,
      note: d.note ?? null,
      successors: d.successorIds.map((id) => ({ to_node_id: id })),
    })) as unknown as Json,
    p_label: meta?.label,
    p_rationale: meta?.rationale,
  });
  if (error) throw new Error(friendly(error.message));
  return toImpact(data);
}

export async function getStructureDraftImpact(draftId: string): Promise<StructureDraftImpact> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db.rpc('structure_draft_impact', { p_draft_id: draftId });
  if (error) throw new Error(friendly(error.message));
  return toImpact(data);
}

/** `toNodeId` null = leave these references on the retired unit (an explicit decision). */
export async function resolveStructureReferences(
  draftId: string,
  refType: string,
  refIds: string[],
  fromNodeId: string,
  toNodeId: string | null,
): Promise<StructureDraftImpact> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db.rpc('resolve_structure_references', {
    p_draft_id: draftId,
    p_ref_type: refType,
    p_ref_ids: refIds,
    p_from_node_id: fromNodeId,
    p_to_node_id: toNodeId ?? undefined,
  });
  if (error) throw new Error(friendly(error.message));
  return toImpact(data);
}

export async function authorizeStructureRevision(draftId: string, overrideReason?: string): Promise<AuthorizeResult> {
  const db = getTypedSupabaseClient();
  const { data, error } = await db.rpc('authorize_structure_revision', {
    p_draft_id: draftId,
    p_override_reason: overrideReason?.trim() || undefined,
  });
  if (error) throw new Error(friendly(error.message));
  const r = (data ?? {}) as Rec;
  return {
    revisionId: String(r.revision_id),
    version: String(r.version),
    added: Number(r.added ?? 0),
    changed: Number(r.changed ?? 0),
    retired: Number(r.retired ?? 0),
    referencesMoved: Number(r.references_moved ?? 0),
    unresolvedOverridden: Number(r.unresolved_overridden ?? 0),
  };
}

export async function discardStructureDraft(draftId: string): Promise<void> {
  const db = getTypedSupabaseClient();
  const { error } = await db.rpc('discard_structure_draft', { p_draft_id: draftId });
  if (error) throw new Error(friendly(error.message));
}
